import {
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    AlertOctagon,
    Building2,
    Calendar,
    CheckCircle2,
    Clock,
    FolderOpen,
    Tag,
    UserCheck,
    UserCog,
    Wrench,
} from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext, useRefresh, useUpdate } from 'ra-core';
import { useState } from 'react';
import { ActivityTimeline } from '../activities/ActivityTimeline';
import { useHasPermission } from '../rbac/useHasPermission';

const ReassignButton = () => {
    const record = useRecordContext();
    const [update] = useUpdate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string>('');
    const [reps, setReps] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    if (!record) return null;

    const handleOpen = async () => {
        setOpen(true);
        setSelected(record.assigned_to ? String(record.assigned_to) : '');
        const { data } = await dataProvider.getList('sales', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'first_name', order: 'ASC' },
            filter: {},
        });
        setReps(data);
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await update('service_tickets', {
                id: record.id,
                data: { assigned_to: selected ? Number(selected) : null },
                previousData: record,
            });
            notify('Ticket reassigned', { type: 'success' });
            setOpen(false);
            refresh();
        } catch (error: any) {
            notify(`Error reassigning ticket: ${error.message}`, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button size="sm" variant="outline" onClick={handleOpen}>
                <UserCog className="w-4 h-4 mr-2" /> Reassign
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle>Reassign Ticket</DialogTitle>
                    </DialogHeader>
                    <Select value={selected} onValueChange={setSelected}>
                        <SelectTrigger>
                            <SelectValue placeholder="Choose assignee" />
                        </SelectTrigger>
                        <SelectContent>
                            {reps.map((rep) => (
                                <SelectItem key={rep.id} value={String(rep.id)}>
                                    {rep.first_name} {rep.last_name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={handleConfirm} disabled={loading}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

const PRIORITY_COLORS: Record<string, string> = {
    low:      'bg-muted text-muted-foreground border-border',
    medium:   'bg-info/10 text-info border-info/30',
    high:     'bg-warning/10 text-warning border-warning/30',
    critical: 'bg-destructive/15 text-destructive border-destructive/40',
};

const STATUS_COLORS: Record<string, string> = {
    open:        'bg-info/10 text-info border-info/30',
    in_progress: 'bg-warning/10 text-warning border-warning/30',
    resolved:    'bg-success/10 text-success border-success/30',
    closed:      'bg-muted text-muted-foreground border-border',
};

const FullNameField = () => {
    const record = useRecordContext();
    if (!record) return <span>—</span>;
    return <span className="text-sm font-medium">{record.first_name} {record.last_name}</span>;
};

const TicketActions = () => {
    const record = useRecordContext();
    const [update] = useUpdate();
    const notify = useNotify();
    const refresh = useRefresh();
    const { hasPermission: canManage } = useHasPermission('manage_tickets');

    if (!record) return null;

    const transition = async (newStatus: string, msg: string) => {
        await update('service_tickets', {
            id: record.id,
            data: { status: newStatus, ...(newStatus === 'resolved' ? { resolved_date: new Date().toISOString() } : {}) },
            previousData: record,
        });
        notify(msg, { type: 'success' });
        refresh();
    };

    return (
        <div className="flex items-center gap-2">
            {canManage && <EditButton />}
            {canManage && <ReassignButton />}
            {canManage && record.status === 'open' && (
                <Button size="sm" variant="outline" className="bg-warning text-warning-foreground hover:bg-warning/90 hover:text-warning-foreground"
                    onClick={() => transition('in_progress', 'Ticket moved to In Progress')}>
                    <Clock className="w-4 h-4 mr-2" /> Start Work
                </Button>
            )}
            {canManage && record.status === 'in_progress' && (
                <Button size="sm" variant="outline" className="bg-success text-success-foreground hover:bg-success/90 hover:text-success-foreground"
                    onClick={() => transition('resolved', 'Ticket marked as Resolved')}>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Resolved
                </Button>
            )}
            {canManage && record.status === 'resolved' && (
                <Button size="sm" variant="outline" className="bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                    onClick={() => transition('closed', 'Ticket closed')}>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Close Ticket
                </Button>
            )}
        </div>
    );
};

const ServiceTicketShowContent = () => {
    const record = useRecordContext();
    if (!record) return null;

    const isCritical = record.priority === 'critical';
    const isOverdue = record.due_date && record.status !== 'resolved' && record.status !== 'closed'
        && new Date(record.due_date) < new Date();

    return (
        <div className="space-y-6 mt-4 pb-12 p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b pb-6">
                <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center border-2 ${isCritical ? 'bg-destructive/10 border-destructive/40' : 'bg-warning/10 border-warning/30'}`}>
                        {isCritical
                            ? <AlertOctagon className="w-7 h-7 text-destructive" />
                            : <Wrench className="w-7 h-7 text-warning" />
                        }
                    </div>
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <span className="font-mono font-bold text-primary text-sm">{record.ticket_number}</span>
                            <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_COLORS[record.status] ?? ''}`}>
                                {record.status?.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <Badge variant="outline" className={`text-[10px] font-bold ${PRIORITY_COLORS[record.priority] ?? ''}`}>
                                {record.priority?.toUpperCase()} PRIORITY
                            </Badge>
                            {isOverdue && (
                                <Badge variant="outline" className="text-[10px] font-bold bg-destructive/10 text-destructive border-destructive/40">
                                    ⚠️ OVERDUE
                                </Badge>
                            )}
                        </div>
                        <h1 className="text-2xl font-bold text-foreground">{record.title}</h1>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Details */}
                <div className="flex-1 min-w-0 space-y-4">
                    <Card>
                        <CardHeader className="pb-3 border-b">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Description</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <div className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">
                                {record.description || <span className="text-muted-foreground italic">No description provided.</span>}
                            </div>
                        </CardContent>
                    </Card>

                    {record.resolution_notes && (
                        <Card className="border-success/30">
                            <CardHeader className="pb-3 border-b bg-success/10/30">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-success flex items-center gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Resolution Notes
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4">
                                <div className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">
                                    <TextField source="resolution_notes" />
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <ActivityTimeline resource="service_tickets" recordId={record.id} />
                </div>

                {/* Sidebar */}
                <div className="lg:w-80 xl:w-96 shrink-0 space-y-4">
                    <Card>
                        <CardHeader className="pb-3 border-b">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Details</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4 space-y-4">
                            <div className="flex items-start gap-3">
                                <Tag className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                                <div>
                                    <span className="text-[10px] font-bold text-muted-foreground uppercase block">Category</span>
                                    <span className="text-sm font-medium capitalize">{record.category || '—'}</span>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <UserCheck className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                                <div>
                                    <span className="text-[10px] font-bold text-muted-foreground uppercase block">Assigned To</span>
                                    <ReferenceField source="assigned_to" reference="sales" link={false}>
                                        <FullNameField />
                                    </ReferenceField>
                                </div>
                            </div>
                            {record.company_id && (
                                <div className="flex items-start gap-3">
                                    <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                                    <div>
                                        <span className="text-[10px] font-bold text-muted-foreground uppercase block">Company</span>
                                        <ReferenceField source="company_id" reference="companies" link="show">
                                            <TextField source="name" className="text-sm font-medium" />
                                        </ReferenceField>
                                    </div>
                                </div>
                            )}
                            {record.project_id && (
                                <div className="flex items-start gap-3">
                                    <FolderOpen className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                                    <div>
                                        <span className="text-[10px] font-bold text-muted-foreground uppercase block">Project</span>
                                        <ReferenceField source="project_id" reference="projects" link="show">
                                            <TextField source="name" className="text-sm font-medium" />
                                        </ReferenceField>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-start gap-3">
                                <Calendar className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                                <div>
                                    <span className="text-[10px] font-bold text-muted-foreground uppercase block">Due Date</span>
                                    <span className={`text-sm font-medium ${isOverdue ? 'text-destructive font-bold' : ''}`}>
                                        {record.due_date
                                            ? new Date(record.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : '—'
                                        }
                                    </span>
                                </div>
                            </div>
                            {record.resolved_date && (
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 className="w-4 h-4 text-success mt-0.5 shrink-0" />
                                    <div>
                                        <span className="text-[10px] font-bold text-muted-foreground uppercase block">Resolved</span>
                                        <DateField source="resolved_date" showTime className="text-sm font-medium text-success" />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export const ServiceTicketShow = () => (
    <Show actions={<TicketActions />}>
        <ServiceTicketShowContent />
    </Show>
);
